import { BaseCRUDManager } from './BaseCRUDManager';
import { QueryFilter } from './types';
import { canBypassBulletinTargetRoles } from '../../utilities/orgAccess';

/**
 * Shared query utilities for common patterns across managers
 */

/**
 * Query documents by organisation ID
 */
export async function queryByOrganisationId<T extends { id: string }>(
  collectionName: string,
  organisationId: string,
  additionalFilters?: QueryFilter[],
  options?: {
    orderBy?: Array<{ field: string; direction: 'asc' | 'desc' }>;
    limit?: number;
  }
): Promise<T[]> {
  const filters: QueryFilter[] = [
    { field: 'organisationId', operator: '==', value: organisationId },
    ...(additionalFilters || [])
  ];

  return BaseCRUDManager.query<T>(collectionName, {
    filters,
    orderBy: options?.orderBy,
    limit: options?.limit
  });
}

/**
 * Query documents by organisation ID with role-based filtering
 * Useful for bulletins, announcements, etc.
 */
export async function queryByOrganisationIdWithRole<T extends { id: string }>(
  collectionName: string,
  organisationId: string,
  userRole: string,
  roleField: string = 'targetRoles',
  options?: {
    orderBy?: Array<{ field: string; direction: 'asc' | 'desc' }>;
    limit?: number;
  }
): Promise<T[]> {
  const isAdmin = canBypassBulletinTargetRoles(userRole);
  
  const filters: QueryFilter[] = [
    { field: 'organisationId', operator: '==', value: organisationId }
  ];
  
  if (!isAdmin) {
    filters.push({ field: roleField, operator: 'array-contains', value: userRole });
  }

  return BaseCRUDManager.query<T>(collectionName, {
    filters,
    orderBy: options?.orderBy,
    limit: options?.limit
  });
}

/**
 * Query documents by a field value
 */
export async function queryByField<T extends { id: string }>(
  collectionName: string,
  field: string,
  value: any,
  options?: {
    orderBy?: Array<{ field: string; direction: 'asc' | 'desc' }>;
    limit?: number;
  }
): Promise<T[]> {
  return BaseCRUDManager.query<T>(collectionName, {
    filters: [{ field, operator: '==', value }],
    orderBy: options?.orderBy,
    limit: options?.limit
  });
}

/**
 * Query documents by multiple field values (AND condition)
 */
export async function queryByFields<T extends { id: string }>(
  collectionName: string,
  filters: QueryFilter[],
  options?: {
    orderBy?: Array<{ field: string; direction: 'asc' | 'desc' }>;
    limit?: number;
  }
): Promise<T[]> {
  return BaseCRUDManager.query<T>(collectionName, {
    filters,
    orderBy: options?.orderBy,
    limit: options?.limit
  });
}
